import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { theme } from '@theme/theme';
import { ProtectedRoute } from '@components/auth/ProtectedRoute';
import { useAuth } from '@hooks/useAuth';
import { LoginSelectorPage } from '@pages/LoginSelectorPage';
import { AdminDashboardPage } from '@pages/AdminDashboardPage';
import { OfficerDashboardPage } from '@pages/OfficerDashboardPage';
import { USAJILILoginPage } from '@pages/USAJILILoginPage';
import { USAJILIDashboardPage } from '@pages/USAJILIDashboardPage';
import { AnalyticsLoginPage } from '@pages/AnalyticsLoginPage';
import { AnalyticsPage } from '@pages/analytics/AnalyticsPage';
import { IEBCLoginPage } from '@pages/iebc/IEBCLoginPage';
import { IEBCPage } from '@pages/iebc/IEBCPage';

/** analytics.sdics.tech serves the same build; there, the site is analytics. */
const isAnalyticsHost = window.location.hostname.startsWith('analytics.');

/** iebc.sdics.tech serves the same build; there, the site is the IEBC dashboard. */
const isIEBCHost = window.location.hostname.startsWith('iebc.');

/** Where an unknown path goes. A link pasted with the sentence's full stop
 *  ("/analytics/login.") is opened without it, and anything else under
 *  /analytics or /usajili stays in that app — never the SDICS /login. */
export function fallbackPath(pathname: string, analyticsHost = isAnalyticsHost, iebcHost = isIEBCHost): string {
  const trimmed = pathname.replace(/[.,;:!?)\]'"]+$/, '');
  if (trimmed !== pathname && trimmed !== '') return trimmed;
  if (pathname.startsWith('/analytics')) return '/analytics';
  if (pathname.startsWith('/usajili')) return '/usajili/dashboard';
  if (iebcHost || pathname.startsWith('/iebc')) return '/iebc';
  return analyticsHost ? '/analytics' : '/login';
}

const Fallback: React.FC = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={fallbackPath(pathname) + search} replace />;
};

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * Layout selector for the root "/" route.
 * - ADMINISTRATOR and SUPERVISOR → Admin dashboard with top tabs (Dashboard / Officers / Locations / Reports / Gaps / Verify / Exports)
 * - REGISTRATION_OFFICER → Officer dashboard with bottom mobile-style tabs (Dashboard / Register / Search / Settings)
 */
const RootLayoutRouter: React.FC = () => {
  const { officer } = useAuth();

  if (!officer) {
    // Fallback - ProtectedRoute parent should redirect before this renders
    return <Outlet />;
  }

  if (officer.role === 'ADMINISTRATOR' || officer.role === 'SUPERVISOR') {
    return <AdminDashboardPage />;
  }

  return <OfficerDashboardPage />;
};

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            {/* Public Routes — only admin login via selector */}
            <Route
              path="/login"
              element={
                isIEBCHost ? <Navigate to="/iebc/login" replace />
                  : isAnalyticsHost ? <Navigate to="/analytics/login" replace />
                  : <LoginSelectorPage />
              }
            />
            <Route path="/login/admin" element={<LoginSelectorPage />} />
            {/* Officer login removed — officers use the Android APK */}
            <Route path="/login/officer" element={<Navigate to="/login" replace />} />

            {/* USAJILI Public Route — dedicated login page for USAJILI dashboard */}
            <Route path="/usajili/login" element={<USAJILILoginPage />} />

            {/* Analytics — same accounts as USAJILI. Home (cards, small map,
                summary) and one page per card; the pages handle their own
                auth (redirect to /analytics/login without a session). */}
            <Route path="/analytics/login" element={<AnalyticsLoginPage />} />
            <Route path="/analytics/dashboard" element={<Navigate to="/analytics" replace />} />
            <Route path="/analytics/*" element={<AnalyticsPage />} />

            {/* IEBC (iebc.sdics.tech) — view-only unregistered voters in the
                account's areas. Own sign-in (phone + password); the page
                redirects to /iebc/login without an IEBC session. */}
            <Route path="/iebc/login" element={<IEBCLoginPage />} />
            <Route path="/iebc" element={<IEBCPage />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                isIEBCHost ? (
                  <Navigate to="/iebc" replace />
                ) : isAnalyticsHost ? (
                  <Navigate to="/analytics" replace />
                ) : (
                  <ProtectedRoute>
                    <RootLayoutRouter />
                  </ProtectedRoute>
                )
              }
            />

            {/* USAJILI Dashboard — auth is handled internally by USAJILIProvider
                (calls /api/usajili/me/ and redirects to /usajili/login on failure).
                No ProtectedRoute here — that redirects to /login (SDICS) not /usajili/login. */}
            <Route
              path="/usajili"
              element={<Navigate to="/usajili/dashboard" replace />}
            />
            <Route
              path="/usajili/dashboard"
              element={<USAJILIDashboardPage />}
            />

            {/* Fallback */}
            <Route path="*" element={<Fallback />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
